/**
 * The Archive (from the rail, above Settings): archived notebooks, projects, reports and
 * manuscripts, and notes. They are frozen: they open read-only, and nothing in them changes until
 * they are unarchived. From here they can also be deleted.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, ArchiveRestore, BookOpen, FileText, FolderOpen, Trash } from "lucide-react";
import { MANUSCRIPT_TYPE, formatTimestamp } from "@brainlog/shared";
import { del, errorMessage, get, post } from "@/lib/api";
import { qk, useMe } from "@/lib/queries";
import { openNote, openView, useUi } from "@/lib/store";
import { DeleteManuscriptDialog, setManuscriptArchived } from "@/features/manuscripts/DeleteManuscriptDialog";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { useNoteTitle } from "@/features/crypto/UnlockPanel";

export const archiveKey = ["archive"] as const;

interface ArchivedSpace {
  id: string;
  name: string;
  kind: "notebook" | "project";
  role: string;
  archivedAt: string;
  archivedBy: string | null;
}
interface ArchivedNote {
  id: string;
  title: string;
  kind: string;
  encryptionScopeId: string | null;
  encTitle: string | null;
  workspaceId: string;
  workspaceName: string;
  archivedAt: string;
  archivedBy: string | null;
}
interface ArchivedManuscript {
  id: string;
  title: string;
  type: string;
  workspaceId: string;
  workspaceName: string;
  archivedAt: string;
  archivedBy: string | null;
  canDelete: boolean;
}

/** After archiving or unarchiving: the sidebar, the notes list and the Archive change. */
export async function refreshArchive(qc: ReturnType<typeof useQueryClient>, workspaceId?: string) {
  await Promise.all([
    qc.invalidateQueries({ queryKey: archiveKey }),
    qc.invalidateQueries({ queryKey: qk.workspaces }),
    ...(workspaceId ? [qc.invalidateQueries({ queryKey: qk.tree(workspaceId) }), qc.invalidateQueries({ queryKey: qk.workspace(workspaceId) })] : []),
    qc.invalidateQueries({ queryKey: ["note"] }),
  ]);
}

export async function setArchived(qc: ReturnType<typeof useQueryClient>, what: { type: "note" | "workspace"; id: string; workspaceId?: string; name: string }, archived: boolean) {
  try {
    await post(`/api/${what.type === "note" ? "notes" : "workspaces"}/${what.id}/${archived ? "archive" : "unarchive"}`, {});
    await refreshArchive(qc, what.workspaceId ?? (what.type === "workspace" ? what.id : undefined));
    toast.success(archived ? `“${what.name}” is archived: find it in the Archive.` : `“${what.name}” is back.`);
    return true;
  } catch (e) {
    toast.error(errorMessage(e));
    return false;
  }
}

export function ArchiveView() {
  const qc = useQueryClient();
  const me = useMe();
  const q = useQuery({ queryKey: archiveKey, queryFn: () => get<{ workspaces: ArchivedSpace[]; notes: ArchivedNote[]; manuscripts: ArchivedManuscript[] }>("/api/archive") });
  const [deleting, setDeleting] = useState<ArchivedManuscript | null>(null);
  const when = (a: { archivedAt: string; archivedBy: string | null }) => `Archived ${formatTimestamp(a.archivedAt, me.data?.timezone)}${a.archivedBy ? ` by ${a.archivedBy}` : ""}`;
  // Opens it read-only in the sidebar's place (it is not listed there).
  const openSpace = (w: ArchivedSpace) => useUi.getState().setWorkspace(w.id);
  const spaces = q.data?.workspaces ?? [];
  const notes = q.data?.notes ?? [];
  const manuscripts = q.data?.manuscripts ?? [];
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
        <PageHeader
          eyebrow="All notebooks & projects"
          title="Archive"
          description="Archived notebooks, projects, manuscripts and notes are out of the way and frozen: you can open, read, search and export them, but nothing in them changes until you unarchive them."
        />
        {q.isError && (
          <p className="text-sm text-destructive" role="alert">
            {errorMessage(q.error)}
          </p>
        )}
        <section aria-labelledby="archive-spaces" className="space-y-2">
          <h2 id="archive-spaces" className="text-base font-semibold">
            Notebooks and projects
          </h2>
          {q.data && !spaces.length && <p className="text-sm text-muted-foreground">None archived. To archive one, use its “…” menu in the sidebar.</p>}
          {spaces.length > 0 && (
            <ul className="divide-y rounded-md border" aria-label="Archived notebooks and projects">
              {spaces.map((w) => (
                <li key={w.id} className="flex flex-wrap items-center gap-2 px-3 py-2.5">
                  <Archive className="size-4 text-muted-foreground" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{w.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {w.kind === "notebook" ? "Notebook" : "Project"} · {when(w)}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openSpace(w)}>
                    <FolderOpen /> Open
                  </Button>
                  {w.role === "owner" && (
                    <Button size="sm" variant="ghost" onClick={() => void setArchived(qc, { type: "workspace", id: w.id, name: w.name }, false)}>
                      <ArchiveRestore /> Unarchive
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
        <section aria-labelledby="archive-manuscripts" className="space-y-2">
          <h2 id="archive-manuscripts" className="text-base font-semibold">
            Reports and manuscripts
          </h2>
          {q.data && !manuscripts.length && <p className="text-sm text-muted-foreground">None archived. To archive one, open its Details tab.</p>}
          {manuscripts.length > 0 && (
            <ul className="divide-y rounded-md border" aria-label="Archived reports and manuscripts">
              {manuscripts.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-2 px-3 py-2.5">
                  <FileText className="size-4 text-muted-foreground" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{m.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {MANUSCRIPT_TYPE.labels[m.type as keyof typeof MANUSCRIPT_TYPE.labels] ?? "Manuscript"} · in “{m.workspaceName}” · {when(m)}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openView("manuscript", m.workspaceId, m.title, undefined, m.id)}>
                    <BookOpen /> Open
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => void setManuscriptArchived(qc, m, false)}>
                    <ArchiveRestore /> Unarchive
                  </Button>
                  {m.canDelete && (
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDeleting(m)}>
                      <Trash /> Delete…
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
        <section aria-labelledby="archive-notes" className="space-y-2">
          <h2 id="archive-notes" className="text-base font-semibold">
            Notes
          </h2>
          {q.data && !notes.length && <p className="text-sm text-muted-foreground">None archived. To archive a note, use its “…” menu.</p>}
          {notes.length > 0 && (
            <ul className="divide-y rounded-md border" aria-label="Archived notes">
              {notes.map((n) => (
                <ArchivedNoteRow key={n.id} n={n} when={when(n)} />
              ))}
            </ul>
          )}
        </section>
      </div>
      {deleting && <DeleteManuscriptDialog manuscript={deleting} onClose={() => setDeleting(null)} />}
    </div>
  );
}

function ArchivedNoteRow({ n, when }: { n: ArchivedNote; when: string }) {
  const qc = useQueryClient();
  const title = useNoteTitle(n);
  return (
    <li className="flex flex-wrap items-center gap-2 px-3 py-2.5">
      <Archive className="size-4 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">
          In “{n.workspaceName}” · {when}
        </p>
      </div>
      <Button size="sm" variant="outline" onClick={() => openNote(n.id, title, n.workspaceId)}>
        <BookOpen /> Open
      </Button>
      <Button size="sm" variant="ghost" onClick={() => void setArchived(qc, { type: "note", id: n.id, workspaceId: n.workspaceId, name: title }, false)}>
        <ArchiveRestore /> Unarchive
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="text-destructive"
        onClick={async () => {
          try {
            await del(`/api/notes/${n.id}`);
            await refreshArchive(qc, n.workspaceId);
            await qc.invalidateQueries({ queryKey: ["trash", n.workspaceId] });
            toast.success(`“${title}” is in the Trash of “${n.workspaceName}”: restore it or delete it for good from there.`);
          } catch (e) {
            toast.error(errorMessage(e));
          }
        }}
      >
        <Trash /> Move to trash
      </Button>
    </li>
  );
}
